/**
 * Public company-branded careers page — /jobs/company/:slug
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, MapPin, Shield, ArrowRight, Building2, Globe } from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.ccp-root{background:#FAF7F0;min-height:100vh;color:#0D1B2A;font-family:'DM Sans',-apple-system,sans-serif;}
.ccp-shell{max-width:1100px;margin:0 auto;padding:48px 28px 96px;}
@media(max-width:680px){.ccp-shell{padding:32px 18px 64px;}}
.ccp-hero{background:#fff;border:1px solid #E8DFC9;border-left:4px solid #C8A84B;padding:48px 44px;margin-bottom:32px;display:flex;gap:32px;align-items:center;flex-wrap:wrap;}
.ccp-logo{flex-shrink:0;width:96px;height:96px;border:1px solid #E8DFC9;background:#FAF7F0;display:flex;align-items:center;justify-content:center;}
.ccp-logo img{max-width:80px;max-height:80px;object-fit:contain;}
.ccp-logo-fb{font-family:'Cormorant Display',serif;font-size:42px;font-weight:700;color:#C8A84B;letter-spacing:-.02em;}
.ccp-eb{font-size:10.5px;font-weight:700;letter-spacing:.28em;color:#C8A84B;text-transform:uppercase;margin-bottom:10px;display:inline-flex;align-items:center;gap:8px;}
.ccp-h1{font-family:'Cormorant Display',serif;font-size:clamp(32px,4.5vw,52px);font-weight:700;line-height:1.04;letter-spacing:-.018em;margin:0 0 8px;color:#0D1B2A;}
.ccp-h1 em{font-style:italic;color:#C8A84B;}
.ccp-meta{display:flex;gap:18px;align-items:center;flex-wrap:wrap;font-size:12.5px;color:#3D4A5C;margin-top:8px;}
.ccp-meta span{display:inline-flex;align-items:center;gap:6px;}
.ccp-verified{background:rgba(200,168,75,.12);border:1px solid #C8A84B;color:#C8A84B;padding:4px 10px;font-size:10px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;display:inline-flex;align-items:center;gap:5px;}

.ccp-about{font-size:15px;line-height:1.65;color:#3D4A5C;margin:0 0 32px;padding:0 4px;max-width:720px;font-weight:300;}

.ccp-jobs-h{font-family:'Cormorant Display',serif;font-size:28px;font-weight:700;color:#0D1B2A;letter-spacing:-.008em;margin:24px 0 16px;}
.ccp-jobs-h em{font-style:italic;color:#C8A84B;}

.ccp-job-card{background:#fff;border:1px solid #E8DFC9;padding:24px 28px;margin-bottom:12px;display:grid;grid-template-columns:1fr auto;gap:20px;align-items:center;transition:transform .2s,box-shadow .2s;}
.ccp-job-card:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(13,27,42,.05);}
@media(max-width:680px){.ccp-job-card{grid-template-columns:1fr;}}
.ccp-job-t{font-family:'Cormorant Display',serif;font-size:22px;font-weight:700;color:#0D1B2A;margin:0 0 4px;letter-spacing:-.005em;}
.ccp-job-meta{font-size:13px;color:#3D4A5C;display:flex;gap:14px;flex-wrap:wrap;}
.ccp-job-meta span{display:inline-flex;align-items:center;gap:5px;}
.ccp-job-cta{display:inline-flex;align-items:center;gap:8px;background:#0D1B2A;color:#C8A84B;padding:12px 22px;font-size:11px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;transition:all .2s;}
.ccp-job-cta:hover{background:#C8A84B;color:#0D1B2A;}
.ccp-empty{padding:48px;text-align:center;color:#7A8190;font-style:italic;font-family:'Cormorant Display',serif;font-size:18px;}
.ccp-err{padding:48px;text-align:center;color:#ad6240;background:rgba(173,98,64,.06);border-left:3px solid #ad6240;}
`;

export default function CompanyCareersPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useSEO({
    title: data?.company?.name ? `Careers at ${data.company.name} · SKI Axis Jobs` : 'Company Careers · SKI Axis',
    description: data?.company?.about || `Open roles at ${slug}. Powered by SKI Axis.`,
  });

  useEffect(() => {
    axios
      .get(`${API}/jobs/companies/${encodeURIComponent(slug)}/careers`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.status === 404 ? 'not-found' : 'error'));
  }, [slug]);

  if (err === 'not-found') {
    return (
      <div className="ccp-root" data-testid="company-careers-404">
        <style>{STYLES}</style><Header />
        <div className="ccp-shell">
          <div className="ccp-err">No active careers page for <strong>{slug}</strong>. <Link to="/jobs" style={{ color: '#C8A84B' }}>Browse all jobs →</Link></div>
        </div><Footer />
      </div>
    );
  }
  if (!data) return <div className="ccp-root"><style>{STYLES}</style><Header /><div className="ccp-shell"><p style={{ color: '#7A8190' }}>Loading…</p></div><Footer /></div>;

  const { company, jobs } = data;
  const initial = (company.name || '').charAt(0).toUpperCase();

  return (
    <div className="ccp-root" data-testid="company-careers-page">
      <style>{STYLES}</style>
      <Header />
      <div className="ccp-shell">
        {/* HERO */}
        <div className="ccp-hero" data-testid="company-hero">
          <div className="ccp-logo">
            {company.logo_url ? <img src={company.logo_url} alt={company.name} /> : <span className="ccp-logo-fb">{initial}</span>}
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div className="ccp-eb">
              <Building2 size={12} /> Careers
              {company.is_verified && (
                <span className="ccp-verified" data-testid="company-verified-badge">
                  <Shield size={10} /> Verified on SKI Axis
                </span>
              )}
            </div>
            <h1 className="ccp-h1">Careers at <em>{company.name}</em></h1>
            <div className="ccp-meta">
              {company.headquarters && <span><MapPin size={12} /> {company.headquarters}</span>}
              {company.website && <span><Globe size={12} /> <a href={company.website} target="_blank" rel="noopener noreferrer" style={{ color: '#C8A84B', textDecoration: 'none' }}>{company.website.replace(/^https?:\/\//, '')}</a></span>}
              <span><Briefcase size={12} /> {data.active_jobs_count} open role{data.active_jobs_count === 1 ? '' : 's'}</span>
            </div>
          </div>
        </div>

        {company.about && <p className="ccp-about">{company.about}</p>}

        <h2 className="ccp-jobs-h">Open <em>roles</em></h2>

        {jobs.length === 0 ? (
          <div className="ccp-empty">No open roles at this time.</div>
        ) : (
          jobs.map((j) => (
            <div key={j.id} className="ccp-job-card" data-testid={`ccp-job-${j.id}`}>
              <div>
                <h3 className="ccp-job-t">{j.title}</h3>
                <div className="ccp-job-meta">
                  {j.location && <span><MapPin size={12} /> {j.location}</span>}
                  {j.employment_type && <span>{j.employment_type}</span>}
                  {j.salary_range && <span>{j.salary_range}</span>}
                </div>
              </div>
              <Link to={`/jobs/${j.id}`} className="ccp-job-cta" data-testid={`ccp-apply-${j.id}`}>
                View & Apply <ArrowRight size={12} />
              </Link>
            </div>
          ))
        )}
      </div>
      <Footer />
    </div>
  );
}
